import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Plus, Edit, Trash2, Search, Eye, CheckCircle, XCircle, History } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmt = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);

const HRAdvances = () => {
  const [advances, setAdvances] = useState([]);
  const [summary, setSummary] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [fullHistoryData, setFullHistoryData] = useState(null);
  const [fullHistoryEmployee, setFullHistoryEmployee] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [form, setForm] = useState({
    employee_name: "", amount: "", reason: "", date: new Date().toISOString().split("T")[0],
    installments: "1", deduct_from_month: String(new Date().getMonth() + 1), deduct_from_year: String(new Date().getFullYear()), notes: "",
  });

  const fetchData = useCallback(async () => {
    const [advRes, sumRes, empRes] = await Promise.all([
      axios.get(`${API}/hr/advances`),
      axios.get(`${API}/hr/advances/summary`),
      axios.get(`${API}/hr/employees`),
    ]);
    setAdvances(advRes.data);
    setSummary(sumRes.data);
    setEmployees(empRes.data);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.employee_name || !form.amount) { toast.error("Employee and amount required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/hr/advances/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/hr/advances`, form);
        toast.success("Advance request recorded");
      }
      setShowDialog(false); setEditing(null); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this advance record?")) return;
    await axios.delete(`${API}/hr/advances/${id}`);
    toast.success("Deleted"); fetchData();
  };

  const handleCreditAdvance = async (adv) => {
    if (!window.confirm(`Mark ₹${adv.amount} advance as credited to ${adv.employee_name}?`)) return;
    try {
      await axios.put(`${API}/hr/advances/${adv.id}`, { status: "Credited" });
      toast.success(`Advance credited to ${adv.employee_name}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleRejectAdvance = async (adv) => {
    if (!window.confirm(`Reject advance request for ${adv.employee_name}?`)) return;
    try {
      await axios.put(`${API}/hr/advances/${adv.id}`, { status: "Rejected" });
      toast.success("Advance request rejected");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const loadFullHistory = async (empName) => {
    try {
      const params = empName ? { employee_name: empName } : {};
      const res = await axios.get(`${API}/hr/advances/full-history`, { params });
      setFullHistoryData(res.data);
      setFullHistoryEmployee(empName || "All Employees");
      setShowFullHistory(true);
    } catch { toast.error("Failed to load history"); }
  };

  const filtered = advances.filter(a => {
    if (filterStatus !== "All" && a.status !== filterStatus) return false;
    if (search && !a.employee_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const statusBadge = (s) => ({
    "Requested": "bg-purple-900/40 text-purple-400",
    "Pending": "bg-amber-900/40 text-amber-400",
    "Partially Deducted": "bg-blue-900/40 text-blue-400",
    "Fully Deducted": "bg-green-900/40 text-green-400",
    "Rejected": "bg-red-900/40 text-red-400",
  }[s] || "bg-gray-800 text-gray-400");

  const totalPending = summary.reduce((s, r) => s + r.total_remaining, 0);

  return (
    <div className="space-y-4" data-testid="hr-advances-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-teal-400 flex items-center gap-2"><Banknote className="w-5 h-5" /> Employee Advances</h2>
          <p className="text-gray-400 text-sm">Request, approve, track & auto-deduct from payroll</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => loadFullHistory("")}
            className="border-cyan-600 text-cyan-300 hover:bg-cyan-900/30" data-testid="full-history-btn">
            <History className="w-4 h-4 mr-1" /> Full History
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setForm({ employee_name: "", amount: "", reason: "", date: new Date().toISOString().split("T")[0], installments: "1", deduct_from_month: String(new Date().getMonth() + 1), deduct_from_year: String(new Date().getFullYear()), notes: "" }); setShowDialog(true); }}
            className="bg-teal-600 hover:bg-teal-700" data-testid="add-advance-btn">
            <Plus className="w-4 h-4 mr-1" /> Request Advance
          </Button>
        </div>
      </div>

      {/* Summary */}
      {summary.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm">Total Pending: </span>
            <span className="text-amber-400 font-bold font-mono">{fmt(totalPending)}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {summary.filter(s => s.total_remaining > 0).map(s => (
              <Card key={s.employee_name} className="bg-gray-900/30 border-gray-700 cursor-pointer hover:border-teal-500/30" onClick={() => loadFullHistory(s.employee_name)}>
                <CardContent className="p-3">
                  <div className="text-white text-xs font-medium truncate">{s.employee_name}</div>
                  <div className="text-amber-400 font-mono text-sm font-bold">{fmt(s.total_remaining)}</div>
                  <div className="text-gray-500 text-xs">of {fmt(s.total_amount)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex gap-1">
          {["All", "Requested", "Pending", "Partially Deducted", "Fully Deducted", "Rejected"].map(s => (
            <Button key={s} size="sm" variant={filterStatus === s ? "default" : "outline"} onClick={() => setFilterStatus(s)}
              className={filterStatus === s ? "bg-teal-700 text-white" : "border-gray-600 text-gray-400 text-xs"}>{s}</Button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee..." className="bg-gray-800 border-gray-600 text-white pl-9 h-9 w-48" />
        </div>
      </div>

      {/* Table */}
      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400">Date</TableHead>
                <TableHead className="text-gray-400">Employee</TableHead>
                <TableHead className="text-gray-400">Reason</TableHead>
                <TableHead className="text-gray-400 text-right">Amount</TableHead>
                <TableHead className="text-gray-400 text-right">Deducted</TableHead>
                <TableHead className="text-gray-400 text-right">Balance</TableHead>
                <TableHead className="text-gray-400">Installments</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-8">No advance records.</TableCell></TableRow>
              ) : filtered.map(a => (
                <TableRow key={a.id} className="border-gray-800">
                  <TableCell className="text-gray-300 whitespace-nowrap">{a.date}</TableCell>
                  <TableCell className="text-white font-medium">{a.employee_name}</TableCell>
                  <TableCell className="text-gray-300 text-sm max-w-[120px] truncate">{a.reason || "—"}</TableCell>
                  <TableCell className="text-right text-white font-mono">{fmt(a.amount)}</TableCell>
                  <TableCell className="text-right text-green-400 font-mono">{fmt(a.deducted_total)}</TableCell>
                  <TableCell className="text-right text-amber-400 font-mono font-bold">{fmt(a.remaining)}</TableCell>
                  <TableCell className="text-cyan-400 text-sm">{a.installments > 1 ? `${a.installments} x ${fmt(a.per_installment)}` : "Lump sum"}</TableCell>
                  <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusBadge(a.status)}`}>{a.status}</span></TableCell>
                  <TableCell className="text-right">
                    {/* Approve/Credit button for Requested advances */}
                    {a.status === "Requested" && (
                      <>
                        <Button size="sm" variant="ghost" className="text-green-400 hover:text-green-300 h-8 px-2" title="Approve & Credit" onClick={() => handleCreditAdvance(a)}><CheckCircle className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" title="Reject" onClick={() => handleRejectAdvance(a)}><XCircle className="w-4 h-4" /></Button>
                      </>
                    )}
                    {a.history?.length > 0 && <Button size="sm" variant="ghost" className="text-teal-400 hover:text-teal-300 h-8 px-2" onClick={() => setShowHistory(a)}><Eye className="w-4 h-4" /></Button>}
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2" onClick={() => {
                      setEditing(a); setForm({ employee_name: a.employee_name, amount: String(a.amount), reason: a.reason || "", date: a.date, installments: String(a.installments), deduct_from_month: String(a.deduct_from_month || ""), deduct_from_year: String(a.deduct_from_year || ""), notes: a.notes || "" }); setShowDialog(true);
                    }}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => handleDelete(a.id)}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader><DialogTitle className="text-teal-400">{editing ? "Edit" : "Request"} Advance</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Employee</Label>
              <Select value={form.employee_name} onValueChange={v => setForm({...form, employee_name: v})} disabled={!!editing}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600 max-h-60">{employees.map(e => <SelectItem key={e.name} value={e.name} className="text-white">{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300">Amount</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="5000" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1 [color-scheme:dark]" /></div>
            </div>
            <div><Label className="text-gray-300">Reason</Label><Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="e.g., Medical emergency, Personal" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-300">Installments</Label>
                <Input type="number" min="1" value={form.installments} onChange={e => setForm({...form, installments: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" />
                {parseInt(form.installments) > 1 && form.amount && (
                  <span className="text-xs text-cyan-400 mt-1 block">{fmt(parseFloat(form.amount) / parseInt(form.installments))}/month</span>
                )}
              </div>
              <div>
                <Label className="text-gray-300">Deduct From</Label>
                <Select value={form.deduct_from_month} onValueChange={v => setForm({...form, deduct_from_month: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">{months.map((m, i) => <SelectItem key={i} value={String(i + 1)} className="text-white">{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Year</Label>
                <Select value={form.deduct_from_year} onValueChange={v => setForm({...form, deduct_from_year: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-gray-300">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            {!editing && (
              <div className="bg-purple-950/30 border border-purple-500/20 rounded-lg p-3 text-xs text-gray-300">
                Status will be <span className="text-purple-400 font-medium">"Requested"</span>. After crediting the advance to the employee, click the <CheckCircle className="w-3 h-3 inline text-green-400" /> approve button to mark as credited. Payroll will then auto-deduct.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deduction History Dialog */}
      {showHistory && (
        <Dialog open={!!showHistory} onOpenChange={() => setShowHistory(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white">
            <DialogHeader><DialogTitle className="text-teal-400">Deduction History — {showHistory.employee_name}</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <div className="text-sm text-gray-400">Advance: {fmt(showHistory.amount)} | Deducted: {fmt(showHistory.deducted_total)} | Balance: {fmt(showHistory.remaining)}</div>
              {showHistory.credited_date && <div className="text-xs text-green-400">Credited on: {showHistory.credited_date}</div>}
              <Table>
                <TableHeader><TableRow className="border-gray-700">
                  <TableHead className="text-gray-400">Month</TableHead>
                  <TableHead className="text-gray-400 text-right">Deducted</TableHead>
                  <TableHead className="text-gray-400">Date</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(showHistory.history || []).map((h, i) => (
                    <TableRow key={i} className="border-gray-800">
                      <TableCell className="text-white">{months[h.month - 1]} {h.year}</TableCell>
                      <TableCell className="text-right text-green-400 font-mono">{fmt(h.amount)}</TableCell>
                      <TableCell className="text-gray-400 text-xs">{h.date?.split("T")[0]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Full History Dialog */}
      <Dialog open={showFullHistory} onOpenChange={v => { if (!v) setShowFullHistory(false); }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <History className="w-5 h-5" /> Full Advance History — {fullHistoryEmployee}
            </DialogTitle>
          </DialogHeader>
          {fullHistoryData && (
            <div className="space-y-4">
              {/* Employee filter */}
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-gray-400 text-xs">Filter by Employee</Label>
                  <Select value={fullHistoryEmployee === "All Employees" ? "" : fullHistoryEmployee} onValueChange={v => loadFullHistory(v)}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-48"><SelectValue placeholder="All Employees" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                      <SelectItem value="all" className="text-white">All Employees</SelectItem>
                      {employees.map(e => <SelectItem key={e.name} value={e.name} className="text-white">{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* All advances with full details */}
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400">Employee</TableHead>
                    <TableHead className="text-gray-400">Reason</TableHead>
                    <TableHead className="text-gray-400 text-right">Amount</TableHead>
                    <TableHead className="text-gray-400 text-right">Deducted</TableHead>
                    <TableHead className="text-gray-400 text-right">Balance</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Credited</TableHead>
                    <TableHead className="text-gray-400">Deduction Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(fullHistoryData.advances || []).map(a => (
                    <TableRow key={a.id} className="border-gray-800">
                      <TableCell className="text-gray-300 whitespace-nowrap">{a.date}</TableCell>
                      <TableCell className="text-white font-medium">{a.employee_name}</TableCell>
                      <TableCell className="text-gray-300 text-sm">{a.reason || "—"}</TableCell>
                      <TableCell className="text-right text-white font-mono">{fmt(a.amount)}</TableCell>
                      <TableCell className="text-right text-green-400 font-mono">{fmt(a.deducted_total)}</TableCell>
                      <TableCell className="text-right text-amber-400 font-mono font-bold">{fmt(a.remaining)}</TableCell>
                      <TableCell><span className={`px-2 py-0.5 rounded text-xs ${statusBadge(a.status)}`}>{a.status}</span></TableCell>
                      <TableCell className="text-xs">{a.credited ? <span className="text-green-400">{a.credited_date || "Yes"}</span> : <span className="text-gray-600">No</span>}</TableCell>
                      <TableCell className="text-xs text-gray-400">
                        {(a.history || []).map((h, i) => (
                          <div key={i}>{months[h.month - 1]} {h.year}: <span className="text-green-400">{fmt(h.amount)}</span></div>
                        ))}
                        {(!a.history || a.history.length === 0) && "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRAdvances;
